import { useRef, useState } from "react";
import { Check, Copy } from "@phosphor-icons/react";

import { LoadingSpinner } from "@/components/common/loading-spinner";
import { siteBaseUrl, studioBaseUrl } from "@/config/env";
import {
  addAuthorizedPublisher,
  createAdvertiser,
  grantAdvertiserCapability,
  type CreateAdvertiserResponse,
} from "@/lib/admin-api";
import {
  canSubmitWizard,
  emptyWizardForm,
  handoverBlurb,
  HANDOVER_TIMING_NOTE,
  initialWizardProgress,
  looksLikeEmail,
  mapAdvertiserError,
  MIN_REASON_LENGTH,
  normalizeHandle,
  parseQuotaLimit,
  resolveWindowToInstant,
  stepsToRunFrom,
  wizardFailedStep,
  wizardSucceeded,
  WINDOW_PRESET_DAYS,
  windowPresetLabel,
  type WizardFormState,
  type WizardProgress,
  type WizardStepId,
} from "@/lib/advertisers";

import { CredentialHandover } from "./credential-handover";
import styles from "./advertisers-page.module.css";
import wizardStyles from "./advertiser-wizard.module.css";

/**
 * The new-advertiser panel: one form, one submit, a spinner, then the
 * credential panel.
 *
 * # WHAT AN ADVERTISER IS
 *
 * A plain FREE user plus a capability grant. Their handle and display name ARE
 * the brand — there is no squad, no brand record, no separate publisher
 * identity — and their publishes attribute to them automatically
 * (content-service#205). Both are set at account creation now: auth-service's
 * Register materializes the user-service profile inline, under the handle and
 * display name typed on this form (auth-service#81), so there is no longer a
 * later step that applies them.
 *
 * # THREE CALLS, ONE SPINNER
 *
 * Provisioning is three operations against three systems in a fixed order —
 * the account, the grant, then sponsored-link disclosure — and each needs the
 * id the one before it produced. None of that is shown: the operator sees a
 * form, then a spinner, then the credential panel. A step-by-step readout
 * used to sit here; it named the mechanics of provisioning rather than
 * answering anything the operator asked, so it is gone. What is still true
 * underneath is that this is three separate calls with no transaction between
 * them, and that shows up only when one of them fails — see below.
 *
 * # WHY THE SPONSORED-LINKS CALL IS PART OF THIS AND NOT A LATER CHORE
 *
 * An advertiser's outbound links have to carry rel="sponsored noopener" on
 * BOTH surfaces. The baked HTML gets it from the grant stamp at publish time;
 * the hydrated reader gets it from content-service's publisher-level
 * sponsored_links flag. Only the second needs an admin action, so this form
 * takes it — an advertiser provisioned without it looks completely fine until
 * somebody reads the rendered page.
 *
 * # PARTIAL FAILURE
 *
 * Three systems, no transaction. Each step reports done or failed on its own,
 * a failure shows ONE inline error naming what failed, and a single retry
 * resumes from there — no step that has already succeeded is ever re-run,
 * Register in particular is not idempotent. The credential panel appears the
 * moment the account exists, not at the end, because a chain that fails at
 * the grant call must not take the only copy of the temporary password down
 * with it.
 */
export interface AdvertiserWizardProps {
  onClose: () => void;
  /** Called once the chain has created something worth reloading the list for. */
  onProvisioned: () => void;
}

export function AdvertiserWizard({ onClose, onProvisioned }: AdvertiserWizardProps) {
  const [form, setForm] = useState<WizardFormState>(emptyWizardForm);
  const [customDateInput, setCustomDateInput] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);

  const [progress, setProgress] = useState<WizardProgress>(initialWizardProgress);
  const [started, setStarted] = useState(false);
  const [running, setRunning] = useState(false);
  const [created, setCreated] = useState<CreateAdvertiserResponse | null>(null);
  const [handoverDismissed, setHandoverDismissed] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  // Refs, not state, for everything the chain reads DURING a run: setState is
  // asynchronous, and a guard that reads a value React has not committed yet
  // is not a guard.
  const progressRef = useRef<WizardProgress>(initialWizardProgress());
  const runningRef = useRef(false);
  // The account this wizard instance created, if any. This is the guard that
  // matters: auth-service's Register is NOT idempotent, so a second call with
  // the same email fails and a second call with a different one creates an
  // orphan account nothing will ever link back. Nothing may call it while this
  // is set.
  const createdRef = useRef<CreateAdvertiserResponse | null>(null);
  // Resolved once, at submit, so the grant's expiry and the expiry quoted in
  // the handover blurb cannot drift apart across a retry.
  const expiryRef = useRef<Date | null>(null);

  const handle = normalizeHandle(form.handle);
  const brandName = form.brandName.trim();
  const quotaLimit = parseQuotaLimit(form);
  const succeeded = wizardSucceeded(progress);
  const failedStep = wizardFailedStep(progress);

  function updateForm(patch: Partial<WizardFormState>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function setStep(id: WizardStepId, status: WizardProgress[WizardStepId]) {
    progressRef.current = { ...progressRef.current, [id]: status };
    setProgress(progressRef.current);
  }

  async function runStep(id: WizardStepId) {
    const reason = form.reason.trim();

    if (id === "account") {
      // THE double-create guard. Register is not idempotent, so once this
      // wizard has called it, nothing may call it again — including a retry of
      // this very step, which is reachable: the step below fails AFTER the
      // account exists when no id comes back with it.
      if (createdRef.current) {
        if (!createdRef.current.userId) throw new Error(NO_ACCOUNT_ID_MESSAGE);
        return;
      }
      const response = await createAdvertiser({
        email: form.email.trim(),
        username: handle,
        displayName: brandName,
      });
      createdRef.current = response;
      setCreated(response);
      // The account is real either way — the credential panel is already
      // showing — but without an id there is nothing to target the remaining
      // steps at, so this step has not done its job.
      if (!response.userId) throw new Error(NO_ACCOUNT_ID_MESSAGE);
      return;
    }

    const userId = createdRef.current?.userId;
    if (!userId) throw new Error(NO_ACCOUNT_ID_MESSAGE);

    if (id === "grant") {
      if (quotaLimit === null || expiryRef.current === null) {
        throw new Error("The allowance or the window is missing.");
      }
      await grantAdvertiserCapability({
        principalId: userId,
        quotaLimit,
        expiresAt: expiryRef.current.toISOString(),
        reason,
      });
      return;
    }

    // EXACTLY sponsored_links. The other two grants are left undefined so
    // they are omitted from the body entirely: admin-service forwards them
    // as pointers and content-service reads an absent field as "leave
    // alone", so sending false here would be an explicit revoke of
    // privileges this flow never asked about.
    await addAuthorizedPublisher(userId, sponsoredLinksNote(handle), { sponsoredLinks: true });
  }

  async function runFrom(from: WizardStepId) {
    if (runningRef.current) return;
    runningRef.current = true;
    setRunning(true);
    try {
      for (const id of stepsToRunFrom(progressRef.current, from)) {
        setStep(id, { state: "running", error: null, field: null });
        try {
          await runStep(id);
          setStep(id, { state: "done", error: null, field: null });
        } catch (err) {
          const mapped = mapAdvertiserError(id, err);
          setStep(id, { state: "failed", error: mapped.message, field: mapped.field });
          // Every later step needs what this one was supposed to produce, so
          // the chain stops here rather than piling failure on failure.
          return;
        }
      }
    } finally {
      runningRef.current = false;
      setRunning(false);
      if (createdRef.current) onProvisioned();
    }
  }

  function handleSubmit() {
    if (!canSubmitWizard(form) || form.choice === null || started) return;
    expiryRef.current = resolveWindowToInstant(form.choice);
    setStarted(true);
    void runFrom("account");
  }

  const blurb =
    created && expiryRef.current
      ? handoverBlurb({
          email: created.email,
          handle,
          brandName,
          siteUrl: siteBaseUrl(),
          studioUrl: studioBaseUrl(),
          quotaLimit: quotaLimit ?? 0,
          expiresAt: expiryRef.current,
        })
      : "";

  async function handleCopyBlurb() {
    try {
      const write = navigator.clipboard?.writeText;
      if (!write) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(blurb);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    }
  }

  return (
    <section className={styles.card} aria-labelledby="advertiser-wizard-title">
      <header className={wizardStyles.header}>
        <h2 className={styles.cardTitle} id="advertiser-wizard-title">
          New advertiser
        </h2>
        <button type="button" className={styles.secondaryButton} onClick={onClose}>
          {succeeded ? "Done" : "Cancel"}
        </button>
      </header>
      <p className={styles.cardHint}>
        Creates the account, issues the publishing grant, and turns on sponsored-link disclosure.
      </p>

      {started ? null : (
        <>
          <div className={styles.formGrid}>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Email</span>
              <input
                className={styles.input}
                type="email"
                value={form.email}
                placeholder="partner@example.com"
                onChange={(event) => updateForm({ email: event.currentTarget.value })}
                onBlur={() => setEmailTouched(true)}
              />
              {emailTouched && form.email.trim() !== "" && !looksLikeEmail(form.email) ? (
                <span className={styles.errorText}>That is not a valid email address.</span>
              ) : null}
            </label>
            <label className={styles.field}>
              <span className={styles.fieldLabel}>Brand name</span>
              <input
                className={styles.input}
                type="text"
                value={form.brandName}
                placeholder="Acme Travel"
                onChange={(event) => updateForm({ brandName: event.currentTarget.value })}
              />
            </label>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Handle</span>
              <input
                className={styles.input}
                type="text"
                value={form.handle}
                placeholder="acmetravel"
                aria-label="Handle"
                onChange={(event) => updateForm({ handle: event.currentTarget.value })}
              />
              <p className={styles.fieldHint}>They publish as @{handle || "…"}.</p>
            </div>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Post allowance</span>
            <div className={styles.quotaRow}>
              <input
                className={styles.quotaInput}
                type="number"
                min={1}
                inputMode="numeric"
                value={form.quotaInput}
                disabled={form.unmetered}
                placeholder="e.g. 25"
                aria-label="Post allowance"
                onChange={(event) => updateForm({ quotaInput: event.currentTarget.value })}
              />
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={form.unmetered}
                  onChange={(event) => updateForm({ unmetered: event.currentTarget.checked })}
                />
                <span>Unmetered</span>
              </label>
            </div>
            <p className={styles.fieldHint}>Total posts over the life of the grant.</p>
          </div>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Window</span>
            <div className={styles.chipRow}>
              {WINDOW_PRESET_DAYS.map((days) => (
                <button
                  key={days}
                  type="button"
                  className={
                    form.choice?.kind === "preset" && form.choice.days === days
                      ? styles.chipActive
                      : styles.chip
                  }
                  onClick={() => updateForm({ choice: { kind: "preset", days } })}
                >
                  {windowPresetLabel(days)}
                </button>
              ))}
              <button
                type="button"
                className={form.choice?.kind === "custom" ? styles.chipActive : styles.chip}
                onClick={() =>
                  updateForm({
                    choice: {
                      kind: "custom",
                      date: customDateInput
                        ? new Date(`${customDateInput}T00:00:00.000Z`)
                        : new Date(Number.NaN),
                    },
                  })
                }
              >
                Custom date
              </button>
            </div>
            {form.choice?.kind === "custom" ? (
              <input
                className={styles.input}
                type="date"
                aria-label="Grant end date"
                value={customDateInput}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(event) => {
                  const value = event.currentTarget.value;
                  setCustomDateInput(value);
                  updateForm({
                    choice: {
                      kind: "custom",
                      date: value ? new Date(`${value}T00:00:00.000Z`) : new Date(Number.NaN),
                    },
                  });
                }}
              />
            ) : null}
          </div>

          <label className={styles.field}>
            <span className={styles.fieldLabel}>Reason</span>
            <textarea
              className={styles.textarea}
              rows={2}
              value={form.reason}
              placeholder="e.g. Q3 campaign, Acme Travel"
              onChange={(event) => updateForm({ reason: event.currentTarget.value })}
              onBlur={() => setReasonTouched(true)}
            />
            {reasonTouched && form.reason.trim().length < MIN_REASON_LENGTH ? (
              <span className={styles.errorText}>Reason must be at least 3 characters.</span>
            ) : null}
          </label>

          <button
            type="button"
            className={styles.primaryButton}
            disabled={!canSubmitWizard(form)}
            onClick={handleSubmit}
          >
            Create advertiser
          </button>
        </>
      )}

      {/* In flight: one spinner, no per-call breakdown. Runs the whole three-call
       * chain, including any retry — see the module doc for why nothing more
       * granular is shown. */}
      {started && running ? <LoadingSpinner size={20} label="Creating advertiser…" /> : null}

      {/* Failed: ONE inline error naming what failed, an inline fix when the
       * failure is a taken email or handle, and a single retry that resumes
       * from the failed call — see stepsToRunFrom and the double-create guard
       * in runStep above. */}
      {started && !running && failedStep !== null ? (
        <div className={wizardStyles.failure}>
          <p className={styles.errorText} role="alert">
            {progress[failedStep].error}
          </p>
          {progress[failedStep].field === "email" || progress[failedStep].field === "handle" ? (
            <label className={styles.field}>
              <span className={styles.fieldLabel}>
                {progress[failedStep].field === "email" ? "Email" : "Handle"}
              </span>
              <input
                className={styles.input}
                type="text"
                aria-label={progress[failedStep].field === "email" ? "Fix email" : "Fix handle"}
                value={progress[failedStep].field === "email" ? form.email : form.handle}
                onChange={(event) =>
                  updateForm(
                    progress[failedStep].field === "email"
                      ? { email: event.currentTarget.value }
                      : { handle: event.currentTarget.value },
                  )
                }
              />
            </label>
          ) : null}
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => void runFrom(failedStep)}
          >
            Retry
          </button>
        </div>
      ) : null}

      {/* The credential panel appears as soon as the account exists — NOT at
       * the end of a successful chain. A failure at any later call must not
       * take the only copy of the temporary password with it. */}
      {created && !handoverDismissed ? (
        <CredentialHandover
          kind="temporary-password"
          secret={created.temporaryPassword}
          accountLabel={created.email}
          passwordChangeEnforced={created.passwordChangeEnforced}
          pendingEmailVerification={created.pendingEmailVerification}
          onDismiss={() => setHandoverDismissed(true)}
        />
      ) : null}

      {created && blurb ? (
        <div className={wizardStyles.blurb}>
          <h3 className={styles.cardTitle}>Send them this</h3>
          <p className={styles.cardHint}>{HANDOVER_TIMING_NOTE}</p>
          <pre className={wizardStyles.blurbText} data-testid="handover-blurb">
            {blurb}
          </pre>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => void handleCopyBlurb()}
          >
            {copyState === "copied" ? <Check size={14} weight="bold" /> : <Copy size={14} />}
            <span>{copyState === "copied" ? "Copied" : "Copy message"}</span>
          </button>
          {copyState === "failed" ? (
            <p className={styles.errorText}>
              Couldn&apos;t copy automatically. Select the text above instead.
            </p>
          ) : null}
        </div>
      ) : null}

      {succeeded ? <p className={styles.consequence}>@{handle} is provisioned.</p> : null}

      {started && !running && failedStep === null && !succeeded ? (
        <p className={styles.errorText} role="alert">
          Stopped without finishing. Reopen this advertiser from the list.
        </p>
      ) : null}
    </section>
  );
}

/**
 * NO_ACCOUNT_ID_MESSAGE covers the one shape of failure where the account
 * exists but nothing else can proceed: auth-service#79 makes Register return
 * the created principal's id, and an admin-service build that predates it
 * returns none. There is no recovery inside this form — the id only becomes
 * discoverable once the account turns up in a live principal search — so this
 * says exactly that instead of retrying into a 400 about a missing principal.
 */
const NO_ACCOUNT_ID_MESSAGE =
  "The account was created, but no id came back for it. Nothing can be granted against it until it turns up in a live principal search. Finish this from the list.";

/** The allowlist note is the only record of WHY this principal is exempt from
 * the auto-hide detector, so it names the flow that added it. */
function sponsoredLinksNote(handle: string): string {
  return `advertiser provisioning — sponsored-link disclosure for @${handle} (admin-service#37)`;
}
